<template>
  <div id="page-security">
    <h5 class="m-4 page-header"> >> {{ $t('SECURITY.PAGE_TITLE') }}</h5>

    <m-row class="mb-4">
      <!-- Left: Events -->
      <m-col lg="8">
        <!-- Recent Logins -->
        <memlist-card class="mb-4">
          
          <div class="d-flex align-items-center justify-content-between w-100 mb-4">
            <h5 class="mb-0">{{ $t('SECURITY.RECENT_LOGINS') }}</h5>
            <memlist-button size="sm" @click="refresh_logins">
              {{ $t('SECURITY.REFRESH') }}
            </memlist-button>
          </div>


          <m-row>
            <m-col lg="6" style="margin-left: 0; margin-right: 0; padding-left: 0px;">
              <memlist-card>
                <span class="legend-dot success"></span><strong class="mr-4">{{ $t('SECURITY.SUCCESS') }}</strong>

                <div v-if="login_success.length === 0" class="muted">{{ $t('SECURITY.NONE_FOUND') }}</div>
              
                <div v-for="(e, i) in login_success" :key="'ls-'+i" class="success mt-4">
                  <m-row class="mt-1">
                    <m-col lg="12">
                      <div class="title">
                        {{ e.display_name || e.email || ($t('SECURITY.USER_HASH') + (e.user_id || '')) }}
                      </div>
                    </m-col>
                  </m-row>
                  <m-row class="mt-1">
                    <m-col lg="12">
                      <div class="time">{{ fmt_dt(e.created_at) }} {{ from_now(e.created_at) }}</div>
                    </m-col>
                  </m-row>
                  <m-row class="mt-1">
                    <m-col lg="6">
                      <span v-if="e.ip"><memlist-badge>{{ e.ip }}</memlist-badge></span>
                    </m-col>
                    <m-col lg="6">
                      <span v-if="e.login_method"><memlist-badge>{{ e.login_method }}</memlist-badge></span>
                    </m-col>
                  </m-row>

                  <hr/>
                </div>
              
              </memlist-card>
              
            </m-col>
            <m-col lg="6" style="margin-left: 0; margin-right: 0; padding-right: 0px;">
              <memlist-card>
                <span class="legend-dot danger"></span><strong>{{ $t('SECURITY.FAILED') }}</strong>
                <div v-if="login_success.length === 0" class="muted">{{ $t('SECURITY.NONE_FOUND') }}</div>
              
                <div v-for="(e, i) in login_success" :key="'ls-'+i" class="danger mt-4">
                  <m-row>
                    <m-col lg="12">
                      <div class="title">
                        <div v-if="e.email">{{ e.email }}</div>
                        <div v-else>{{ $t('SECURITY.NO_EMAIL_PROVIDED') }}</div>
                        <div v-if="e.reason"> — {{ e.reason }}</div>
                      </div>
                    </m-col>
                  </m-row>
                  
                  <m-row class="mt-1">
                    <m-col lg="12">
                      <div class="time">{{ fmt_dt(e.created_at) }} {{ from_now(e.created_at) }}</div>
                    </m-col>
                  </m-row>
                  <m-row class="mt-1">
                    <m-col lg="6">
                      <span v-if="e.ip"><memlist-badge>{{ e.ip }}</memlist-badge></span>
                    </m-col>
                    <m-col lg="6">
                      <span v-if="e.login_method"><memlist-badge>{{ e.login_method }}</memlist-badge></span>
                    </m-col>
                  </m-row>

                  <hr/>
                </div>
              </memlist-card>
            </m-col>
          </m-row>
        </memlist-card>


        <!-- Recent Backups -->
        <memlist-card>
          
          <div class="d-flex align-items-center justify-content-between w-100">
            <h5 class="mb-0">{{ $t('SECURITY.RECENT_BACKUPS') }}</h5>
            <div class="d-flex align-items-center">
              <span class="mr-4 muted">
                {{ $t('SECURITY.LAST_BACKUP') }}
                <strong v-if="last_backup_at">{{ from_now(last_backup_at) }}</strong>
                <strong v-else>—</strong>
              </span>
              <memlist-button size="sm" @click="refresh_backups">{{ $t('SECURITY.REFRESH') }}</memlist-button>
              <memlist-button size="sm" class="ml-2" @click="create_backup" variant="success">{{ $t('SECURITY.CREATE_BACKUP') }}</memlist-button>
            </div>
          </div>

          <div>
            <div v-if="backups.length === 0" class="muted">{{ $t('SECURITY.NO_BACKUPS_FOUND') }}</div>
            <ul v-else class="backup-list">
              <li v-for="(b, i) in backups_pretty" :key="'b-'+i" class="backup-row">
                <div class="status">
                  <span :class="['status-dot', b.ok ? 'ok' : 'warn']"></span>
                </div>
                <div class="details">
                  <div class="title">{{ b.filename || b.name || $t('SECURITY.BACKUP') }}</div>
                  <div class="sub">
                    <span>{{ fmt_dt(b.created_at) }}</span>
                    <span v-if="b.size_pretty"> · {{ b.size_pretty }}</span>
                    <span v-if="b.location"> · {{ b.location }}</span>
                  </div>
                </div>
                <div class="right">
                  <div class="age">{{ from_now(b.created_at) }}</div>
                </div>
              </li>
            </ul>
          </div>
        </memlist-card>
      </m-col>

      <!-- Right: Checklist -->
      <m-col lg="4">
        <memlist-card>
          <h5 class="mb-0">{{ $t('SECURITY.LATEST_EXPORTS') }}</h5>

          <div v-if="recent_exports.length === 0" class="muted mt-4">{{ $t('SECURITY.NO_EXPORTS_FOUND') }}</div>

          <div v-if="recent_exports.length" class="mt-4">
            
            <ul class="event-list compact">
              <li v-for="(e, i) in recent_exports" :key="'ex-'+i" class="event-row neutral">
                <div class="left">
                  <div class="title">
                    <a href="#">Ladda ned XLSX (Fil # {{ e.data.file_id }})</a>
                  </div>
                  <div class="sub sub-email">
                    <span v-if="e.display_name">{{ e.display_name }}</span>
                    <span v-if="e.display_name && e.email"> ({{ e.email }})</span>
                    <span v-else-if="e.email">{{ e.email }}</span>
                  </div>
                  <div class="sub">
                    <span>Antal medlemmar exporterade: {{ e.data.num_members }}</span>
                  </div>
                </div>
                <div class="right">
                  <div class="time">{{ fmt_dt(e.created_at) }} {{ from_now(e.created_at) }}</div>
                </div>
              </li>
            </ul>
          </div>
        </memlist-card>
      </m-col>
    </m-row>
  </div>
</template>

<script>
import axios from 'axios';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { toasts } from '@/core/mixins/toastr-helper.mixin.js';

dayjs.extend(relativeTime);
dayjs.extend(customParseFormat);

export default {
  name: 'SecurityEvents',
  data() {
    return {
      backups: [],
      user_events: [],
      loading_backups: false,
      loading_logins: false,
    };
  },
  mixins: [toasts],
  computed: {
    normalized_events() {
      // Normalize to your provided shape
      return (this.user_events || []).map(evt => {
        const email = evt?.user?.email || evt?.data?.email || null;
        const ip = evt?.data?.ip || null;
        const first = evt?.user?.firstname || '';
        const last  = evt?.user?.lastname || '';
        const name  = (first + ' ' + last).trim() || null;

        let reason = null;
        if (evt.type === 'LOGIN_FAIL') {
          const msg = (evt?.data?.message || '').toString();
          if (msg === 'incorrect_password') reason = this.$t('SECURITY.REASON_INCORRECT_PASSWORD');
          else if (msg) reason = msg.replace(/_/g, ' ');
          else reason = this.$t('SECURITY.REASON_AUTH_FAILED');
        }

        return {
          id: evt.id,
          type: evt.type,
          created_at: evt.created_at, // "YYYY-MM-DD HH:mm:ss"
          user_id: evt.user_id,
          email,
          ip,
          login_method: evt.login_method || null,
          display_name: name,
          reason,
          data: evt.data,
        };
      });
    },

    login_success() {
      return this.normalized_events
        .filter(e => e.type === 'LOGIN_SUCCESS')
        .sort((a, b) => this.dt(b.created_at) - this.dt(a.created_at))
        .slice(0, 20);
    },

    login_failed() {
      return this.normalized_events
        .filter(e => e.type === 'LOGIN_FAIL')
        .sort((a, b) => this.dt(b.created_at) - this.dt(a.created_at))
        .slice(0, 20);
    },

    recent_exports() {
      return this.normalized_events
        .filter(e => e.type === 'EXPORT_MEMBERS')
        .sort((a, b) => this.dt(b.created_at) - this.dt(a.created_at))
        .slice(0, 10);
    },

    backups_pretty() {
      return (this.backups || [])
        .sort((a, b) => this.dt(b.created_at) - this.dt(a.created_at))
        .slice(0, 12)
        .map(b => {
          const size = b.size || b.bytes || b.file_size;
          const status = (b.status || '').toString().toLowerCase();
          return {
            ...b,
            size_pretty: this.pretty_bytes(size),
            ok: status ? ['ok', 'success', 'completed'].includes(status) : true,
          };
        });
    },

    last_backup_at() {
      if (!this.backups || this.backups.length === 0) return null;
      const latest = [...this.backups].sort((a, b) => this.dt(b.created_at) - this.dt(a.created_at))[0];
      return latest?.created_at || null;
    },

    checklist() {
      const now = dayjs();
      const lastBackupAt = this.last_backup_at ? this.dt(this.last_backup_at) : null;

      const fails_24h = this.normalized_events.filter(
        e => e.type === 'LOGIN_FAIL' && this.dt(e.created_at) >= now.clone().subtract(24, 'hour')
      ).length;

      const successes_24h = this.normalized_events.filter(
        e => e.type === 'LOGIN_SUCCESS' && this.dt(e.created_at) >= now.clone().subtract(24, 'hour')
      ).length;

      const backups_7d = (this.backups || []).filter(
        b => this.dt(b.created_at) >= now.clone().subtract(7, 'day')
      ).length;

      const exports_30d = this.normalized_events.filter(
        e => e.type === 'EXPORT_MEMBERS' && this.dt(e.created_at) >= now.clone().subtract(30, 'day')
      ).length;

      return [
        {
          key: 'daily-backup',
          label: this.$t('SECURITY.DAILY_BACKUP_TITLE'),
          completed: !!lastBackupAt && now.diff(lastBackupAt, 'hour') <= 24,
          hint: lastBackupAt
            ? this.$t('SECURITY.DAILY_BACKUP_HINT_HAS', { when: this.from_now(lastBackupAt) })
            : this.$t('SECURITY.DAILY_BACKUP_HINT_MISSING'),
        },
        {
          key: 'weekly-backup-frequency',
          label: this.$t('SECURITY.WEEKLY_BACKUP_TITLE'),
          completed: backups_7d >= 5,
          hint: this.$t('SECURITY.WEEKLY_BACKUP_HINT', { count: backups_7d }),
        },
        {
          key: 'failed-login-spike',
          label: this.$t('SECURITY.FAILED_SPIKE_TITLE'),
          completed: fails_24h <= 5,
          hint: this.$t('SECURITY.FAILED_SPIKE_HINT', { count: fails_24h }),
        },
        {
          key: 'recent-activity',
          label: this.$t('SECURITY.RECENT_ACTIVITY_TITLE'),
          completed: successes_24h > 0,
          hint: successes_24h > 0
            ? this.$t('SECURITY.RECENT_ACTIVITY_HINT_HAS', { count: successes_24h })
            : this.$t('SECURITY.RECENT_ACTIVITY_HINT_NONE'),
        },
        {
          key: 'export-review',
          label: this.$t('SECURITY.EXPORT_REVIEW_TITLE'),
          completed: exports_30d === 0,
          hint: exports_30d === 0
            ? this.$t('SECURITY.EXPORT_REVIEW_HINT_NONE')
            : this.$t('SECURITY.EXPORT_REVIEW_HINT_SOME', { count: exports_30d }),
        },
      ];
    },
  },
  mounted() {
    this.refresh_backups();
    this.refresh_logins();
  },
  methods: {
    async create_backup() {
      const res = await axios.post(`/system/backup`, {});

      if (res.status === 201) {
        this.toastr('success', this.$t('COMMON.OK'), this.$t('SECURITY.BACKUP_IN_PROGRESS'));
      }
      else {
        console.error('unable create');
      }
    },
    async refresh_backups() {
      this.loading_backups = true;
      try {
        const res = await axios.get(`/system/backups`);
        if (res.status === 200) this.backups = res.data || [];
      } catch (e) {
        console.error('Failed to load backups', e);
        this.backups = [];
      } finally {
        this.loading_backups = false;
      }
    },
    async refresh_logins() {
      this.loading_logins = true;
      try {
        const types = ['LOGIN_FAIL', 'LOGIN_SUCCESS', 'EXPORT_MEMBERS'];
        const q = encodeURIComponent('[' + types.map(x => `"${x}"`).join(',') + ']');
        const res = await axios.get(`/history/users/bytypes?types=${q}`);
        if (res.status === 200) this.user_events = res.data || [];
      } catch (e) {
        console.error('Failed to load recent user history', e);
        this.user_events = [];
      } finally {
        this.loading_logins = false;
      }
    },

    // Helpers
    dt(v) {
      if (!v) return dayjs(0);
      // Accept "YYYY-MM-DD HH:mm:ss" (API) and also ISO
      if (typeof v === 'string' && v.length === 19 && v.indexOf('T') === -1) {
        return dayjs(v, 'YYYY-MM-DD HH:mm:ss');
      }
      return dayjs(v);
    },
    fmt_dt(v) {
      const d = this.dt(v);
      return d.isValid() ? d.format('YYYY-MM-DD HH:mm:ss') : '—';
    },
    from_now(v) {
      const d = this.dt(v);
      return d.isValid() ? d.fromNow() : '—';
    },
    pretty_bytes(n) {
      if (!n || isNaN(Number(n))) return null;
      const bytes = Number(n);
      const units = ['B','KB','MB','GB','TB'];
      const idx = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
      const val = (bytes / Math.pow(1024, idx)).toFixed(idx === 0 ? 0 : 1);
      return `${val} ${units[idx]}`;
    },
  },
};
</script>

<style lang="scss" scoped>

.muted { color: #6c757d; }

.legend-dot {
  width: 10px; height: 10px; border-radius: 50%; display: inline-block; margin-right: 6px;
  &.success { background: #28a745; }
  &.danger { background: #dc3545; }
}

/* Give left text more room; shrink right meta column slightly */
.event-list { list-style: none; padding: 0; margin: 0; }
.event-list.compact .event-row { padding-top: 8px; padding-bottom: 8px; }

.success .title { color: #1f7a33; }
.danger .title { color: #a71d2a; }


.title {
  font-weight: 600;
  /* allow wrapping instead of hard ellipsis for long checklist-like text */
  white-space: normal;
}

.sub {
  font-size: 12px; color: #6c757d;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
/* Email lines: full width, no ellipsis, wrap long addresses */
.sub-email {
  white-space: normal !important;
  word-break: break-word;
}
.event-row .right {
  flex: 0 0 160px; /* narrower than before so left side gets more width */
  text-align: right;
}
.event-row .age { font-size: 12px; color: #6c757d; }

/* Backups */
.backup-list { list-style: none; padding: 0; margin: 0; }
.backup-row {
  display: grid; grid-template-columns: 20px 1fr 160px; grid-gap: 12px;
  padding: 12px 0; border-bottom: 1px solid #eee; align-items: center;
}
.status-dot { width: 10px; height: 10px; border-radius: 50%; display: inline-block; }
.status-dot.ok { background: #28a745; }
.status-dot.warn { background: #ffc107; }
.details .title { font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.details .sub { font-size: 12px; color: #6c757d; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

/* Checklist: more room for the label so it doesn't wrap unnecessarily */
.checklist { list-style: none; padding: 0; margin: 0; }
.check-item {
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr) auto;
  grid-column-gap: 12px; grid-row-gap: 6px;
  align-items: center; padding: 10px 0; border-bottom: 1px solid #f1f1f1;
}
.check-item .state i { font-size: 18px; }
.check-item .state.done i { color: #28a745; }
.check-item .state.todo i { color: #dc3545; }
.check-item .label {
  font-weight: 600;
  white-space: nowrap; /* keep short labels single-line on wide screens */
}
@media (max-width: 1200px) {
  /* On narrower screens allow wrapping to avoid squash */
  .check-item .label { white-space: normal; }
}
.check-item .hint { font-size: 12px; }
.badge {
  font-size: 12px; padding: 4px 8px; border-radius: 999px; font-weight: 600;
  white-space: nowrap;
}
.badge.done { background: #e7f6ea; color: #1f7a33; }
.badge.todo { background: #fdecea; color: #a71d2a; }

/* Two-column layout for the lists */
.login-grid { display: grid; grid-template-columns: 1fr; grid-gap: 16px; }
@media (min-width: 992px) { .login-grid { grid-template-columns: 1fr 1fr; } }
.login-column h6 { font-weight: 600; }
</style>
